import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Target, Heart, Lightbulb, Users, Globe, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/ui/logo";
import { TeamSection, type TeamMember } from "@/components/about/TeamSection";
import founderPhoto from "@/assets/founder-kushagra.png";

const values = [
  {
    icon: Target,
    title: "Clarity Over Complexity",
    description:
      "We believe the best software starts with clear requirements. Every feature we build is designed to eliminate ambiguity and reduce miscommunication between product and engineering teams.",
  },
  {
    icon: Heart,
    title: "Developer Empathy",
    description:
      "We've been on both sides of the table — writing requirements and building from them. Reqqy exists because we know the pain of unclear specs, and we're committed to fixing it.",
  },
  {
    icon: Lightbulb,
    title: "Continuous Improvement",
    description:
      "Just as we help teams iterate on their requirements, we constantly iterate on our product. Every review processed makes Reqqy smarter and more attuned to what clarity really means.",
  },
  {
    icon: Users,
    title: "Team-First Design",
    description:
      "Requirements are a team sport. From shared personas to organisation-wide projects, Reqqy is built for collaboration, not silos.",
  },
];

const teamMembers: TeamMember[] = [
  {
    name: "Kushagra Mittal",
    initials: "KM",
    role: "Founder & CEO",
    bio: "Kushagra built Reqqy after seeing too many engineering teams struggle with unclear requirements. With a background spanning product and engineering, he's passionate about bridging the gap between what's written and what's built.",
    photo: founderPhoto,
    linkedin: "https://www.linkedin.com/in/kshgr/",
  },
  // Add more team members here as the team grows
];

const stats = [
  { value: "50K+", label: "Requirements reviewed" },
  { value: "2,000+", label: "Teams using Reqqy" },
  { value: "40%", label: "Avg. reduction in rework" },
  { value: "85+", label: "Avg. clarity score after review" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About"
        description="Learn about Reqqy's mission to eliminate unclear requirements and reduce engineering rework. Meet the vision behind the requirements clarity engine."
        canonical="/about"
      />

      {/* Header */}
      <header className="border-b border-border">
        <div className="container flex items-center justify-between h-16 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="full" className="h-8 w-auto" />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/home">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      <main className="container px-4 md:px-6 py-12 md:py-16 max-w-4xl">
        {/* Hero */}
        <section className="mb-16">
          <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">About Reqqy</h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
            We're on a mission to make sure no engineer ever has to guess what a requirement means again.
          </p>
          <div className="p-6 rounded-xl border border-border bg-card">
            <h2 className="text-xl font-semibold mb-3">Our Story</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Reqqy was born from a simple frustration: too many software projects fail not because of bad code, but
                because of bad requirements. Vague user stories, missing acceptance criteria, and unstated assumptions
                create a cascade of rework, missed deadlines, and frustrated teams.
              </p>
              <p>
                We saw teams spending hours in requirements review meetings, only to discover gaps weeks later during
                development. Engineers would interpret ambiguous specs differently. QA would write tests for
                requirements that meant different things to different people. Product managers would wonder why the
                delivered feature didn't match their vision.
              </p>
              <p>
                Reqqy is the solution: an AI-powered requirements clarity engine that catches ambiguity before
                engineering sees it. By simulating the perspectives of engineers, QA leads, designers, and product
                managers, Reqqy surfaces the questions that would otherwise derail your sprint.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map(({ value, label }) => (
              <div key={label} className="text-center p-4 rounded-xl border border-border bg-card">
                <div className="text-2xl md:text-3xl font-bold text-primary mb-1">{value}</div>
                <div className="text-sm text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Our Values</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {values.map(({ icon: Icon, title, description }) => (
              <div key={title} className="p-5 rounded-xl border border-border bg-card">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <TeamSection members={teamMembers} />
        
        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">How Reqqy Works</h2>
          <div className="space-y-4">
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">1. Submit Your Requirements</h3>
              <p className="text-sm text-muted-foreground">
                Paste any requirements text — user stories, feature specs, PRDs, or acceptance criteria. Reqqy accepts
                natural language in any format your team uses.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">2. AI-Powered Multi-Perspective Review</h3>
              <p className="text-sm text-muted-foreground">
                Reqqy analyses your requirements through the lens of configurable personas — simulating how different
                stakeholders would interpret and question your specs. Each persona brings domain-specific concerns and
                evaluation criteria.
              </p>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-semibold mb-2">3. Actionable Results</h3>
              <p className="text-sm text-muted-foreground">
                Receive a clarity score, flagged issues with severity levels, rewritten requirement suggestions, and a
                prioritised list of clarifying questions to resolve before development begins.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="p-8 rounded-xl border border-border bg-muted/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Ready to ship clearer requirements?</h2>
          <p className="text-muted-foreground mb-4">
            Join thousands of teams who use Reqqy to eliminate rework and build what was actually intended.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild>
              <Link to="/auth?mode=signup">
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/contact-sales">Talk to Sales</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
